import { T, useGT } from 'gt-react';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  ARRAY_SCALAR_ITEM_TYPES,
  type ArrayItemTypeSelection,
} from '@erp/shared-utils';
import { cn } from 'src/lib/utils';

type ArrayItemTypeSelectProps = {
  value: ArrayItemTypeSelection;
  onChange?: (value: ArrayItemTypeSelection) => void;
  disabled?: boolean;
  id?: string;
  /** Inline tree row: `array<…>` with no extra label. Form: labeled picker. */
  variant?: 'inline' | 'form';
  /** Show inline type notation without opening a picker (tree table). */
  readOnly?: boolean;
};

const ITEM_TYPE_LABELS: Record<ArrayItemTypeSelection, string> = {
  untyped: 'Untyped',
  string: 'String',
  text: 'Text',
  integer: 'Integer',
  float: 'Float',
  boolean: 'Boolean',
  date: 'Date',
  datetime: 'DateTime',
  json: 'JSON',
  array: 'Nested array',
  object: 'Object',
};

function ArrayItemTypeOptions() {
  return (
    <>
      <SelectItem value="untyped">
        <T context="array item type option untyped">Untyped</T>
      </SelectItem>
      {ARRAY_SCALAR_ITEM_TYPES.map((type) => (
        <SelectItem key={type} value={type}>
          {ITEM_TYPE_LABELS[type]}
        </SelectItem>
      ))}
      <SelectItem value="object">
        <T context="array item type option object">Object</T>
      </SelectItem>
    </>
  );
}

export function ArrayItemTypeSelect({
  value,
  onChange,
  disabled = false,
  id,
  variant = 'form',
  readOnly = false,
}: ArrayItemTypeSelectProps) {
  const gt = useGT();

  if (variant === 'inline') {
    if (readOnly) {
      return (
        <div className="flex flex-wrap items-center gap-0.5 font-mono text-xs text-muted-foreground">
          <span className="text-foreground">
            {gt('array', {
              context: 'type notation prefix in inline array generic display',
            })}
          </span>
          <span>&lt;</span>
          <span className="rounded border border-dashed border-border/80 bg-muted/30 px-2 py-0.5 font-sans text-foreground">
            {ITEM_TYPE_LABELS[value]}
          </span>
          <span>&gt;</span>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-0.5 font-mono text-xs">
        <span className="text-foreground">
          {gt('array', {
            context: 'type notation prefix in inline array generic display',
          })}
        </span>
        <span className="text-muted-foreground">&lt;</span>
        <Select
          value={value}
          onValueChange={(next) => onChange?.(next as ArrayItemTypeSelection)}
          disabled={disabled}
        >
          <SelectTrigger
            id={id}
            className={cn(
              'h-7 w-[9.5rem] border-dashed font-sans text-xs shadow-none',
            )}
            aria-label={gt('Array element type', {
              context: 'aria label inline array element type picker',
            })}
          >
            <SelectValue
              placeholder={gt('Untyped', {
                context: 'placeholder inline array element type picker',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            <ArrayItemTypeOptions />
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">&gt;</span>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        <T context="label for array element type picker in field form">
          Array elements are
        </T>
      </Label>
      <Select
        value={value}
        onValueChange={(next) => onChange?.(next as ArrayItemTypeSelection)}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="h-8 text-xs">
          <SelectValue
            placeholder={gt('Select element type', {
              context: 'placeholder array element type picker',
            })}
          />
        </SelectTrigger>
        <SelectContent>
          <ArrayItemTypeOptions />
        </SelectContent>
      </Select>
    </div>
  );
}
